// Client-side route registry for the Platform web app. URLs here correspond to
// the seeded PLATFORM authorization resources (url column) where applicable.
export const ROUTES = {
  login: '/login',
  forgotPassword: '/forgot-password',
  /** Sign-out entry for product apps: revokes the Platform session, then shows login. */
  logout: '/logout',
  /** Public sign-up: creates a COMMUNITY member (no application menus). */
  register: '/register',
  orgSelect: '/org-select',
  /**
   * The base URL is the **Hub public landing** (Hub design 06, FE-04): hero search, category
   * groups, featured organizations of every product, near you, latest jobs. It is public and reads
   * only the anonymous `/public/*` API through `features/public/publicApi.ts`, which never sends a
   * bearer token, so a stale session cannot turn it into a sign-in loop.
   *
   * The former organization landing (the ported PoseidonFrontend `mainLanding` feed) moved into the
   * workspace at `appFeed` (`/app/feed`).
   */
  landing: '/',
  /** Public directory (Hub design 06). Category/city pages are search pre-filtered by path. */
  search: '/search',
  /**
   * The three main sections are the landing's explore views; these are the URLs Google indexes
   * (sitemap, canonical, header links). `/businesses` and `/jobs` redirect here (301 in web.config).
   */
  exploreBusinesses: '/?type=biz',
  exploreProducts: '/?type=prod',
  exploreJobs: '/?type=job',
  /** Old address of the business directory: redirects to `exploreBusinesses`. */
  businesses: '/businesses',
  /** Biznesi product line (restaurant, cosmetics, jobs, hotel, real estate): `features/public/ProductPages.tsx`. */
  products: '/products',
  product: '/products/:product',
  category: '/c/:categoryCode',
  city: '/city/:city',
  /** Company card target: custom URL → product website → Platform company page (02 §4). */
  company: '/o/:slug',
  /** Old address of the jobs list: redirects to `exploreJobs`. Job details stay at `/jobs/:jobId`. */
  jobs: '/jobs',
  /** Public business posts (PlatformDB 066); `?post=<id>` opens one shared post. */
  social: '/social',
  job: '/jobs/:jobId',
  forBusiness: '/for-business',
  /** The old public landing / organization feed, now inside the workspace. */
  /** The signed-in workspace: where a successful sign-in lands. */
  home: '/app',
  switchApp: '/switch',
  personalAccount: '/me',
  // HUB-UI: design 06 personal hub and workspace routes; migration 050 seeds matching resources.
  personalActivity: '/me/activity',
  personalTimeline: '/me/timeline',
  personalApplications: '/me/applications',
  personalFavourites: '/me/favourites',
  personalAddresses: '/me/addresses',
  personalNotifications: '/me/notifications',
  personalSecurity: '/me/account',
  personalPrivacy: '/me/privacy',
  personalSessions: '/me/account/sessions',
  /** Organization profiles (PlatformDB 056): one business per product. */
  organizationProfiles: '/app/organization-profiles',
  organizationProfile: '/app/organization-profiles/:id',
  organizationProfileListings: '/app/organization-profiles/listings',
  /** Pre-056 links; redirected to the organization profiles. */
  companyProfile: '/app/company',
  companyListings: '/app/company/listings',
  workspaceJobs: '/app/jobs',
  /** Where a business writes its posts (Public ones show on the Hub landing and `/social`). */
  workspacePosts: '/app/posts',
  applicants: '/app/jobs/:id/applicants',
  directoryAdmin: '/app/directory',
  /** Billing (PlatformDB 054): businesses grid, tenant plans, platform-admin review queue. */
  myBusinesses: '/app/companies',
  billing: '/app/billing',
  billingRequests: '/app/billing/requests',
  promotions: '/app/billing/promotions',
  organizationRequests: '/app/organization-requests',
  launchProduct: '/launch/:productCode',
  activity: '/activity',
  profile: '/profile',
  sessions: '/profile/sessions',
  organization: '/organization',

  // ---------------------------------------------------------------------------
  // Ported PoseidonFrontend screens
  // ---------------------------------------------------------------------------
  // One entry per seeded PLATFORM catalog URL (scripts 005 and 010). The copied pages
  // navigate to these exact paths — `RolesDetailsGrid` pushes `/role-actions` — and the
  // legacy shell turns a granted resource's `url` into a link, so the route table and the
  // catalog have to agree. The parity is asserted by `scripts/route-parity-check.ps1`.
  //
  // The ported notifications grid **replaces** the MUI one that used to answer
  // `/notifications`: the product owner asked for the old screens to win over the custom
  // ones, and two catalog rows (`platform.notifications` and
  // `platform.techpos.notifications`) both point at that URL.
  notifications: '/notifications',
  notificationCategories: '/notification-categories',
  notificationUserCategories: '/notification-user-categories',
  currencies: '/currencies',
  integration: '/integration',
  users: '/users',
  roles: '/roles',
  roleActions: '/role-actions',
} as const;

/**
 * Paths that must render without a session.
 *
 * `RequireAuth` never guards these, so it never sets one as the post-login `from` — but a stale
 * link or a hand-typed `state.from` can, and sending a freshly signed-in operator back to the
 * public landing instead of their workspace is exactly the bug this list prevents.
 */
const PUBLIC_PATHS: readonly string[] = [
  ROUTES.landing, ROUTES.login, ROUTES.register, ROUTES.forgotPassword, ROUTES.orgSelect, ROUTES.search, ROUTES.businesses, ROUTES.products, ROUTES.jobs, ROUTES.social, ROUTES.forBusiness,
];
/** Parameterised public Hub routes (`/c/:code`, `/city/:city`, `/o/:slug`, `/jobs/:jobId`, `/products/:product`). */
const PUBLIC_PREFIXES: readonly string[] = ['/c/', '/city/', '/o/', '/jobs/', '/products/'];

/**
 * Where to go after a successful sign-in.
 *
 * A deep link into a protected screen wins (the operator asked for that screen before being
 * interrupted), anything else lands on the workspace at `/app`. The landing is deliberately not
 * a valid target: it is public, so nobody is ever *interrupted* on their way to it.
 */
export function postLoginDestination(from?: string | null): string {
  if (!from) return ROUTES.home;
  const path = from.split('?')[0].split('#')[0];
  if (!path.startsWith('/') || path.startsWith('//')) return ROUTES.home;
  if (isPublicPath(path)) return ROUTES.home;
  return from;
}

/** True when `pathname` is a route that renders for a visitor with no session. */
export function isPublicPath(pathname: string): boolean {
  const path = pathname.split('?')[0].split('#')[0];
  return PUBLIC_PATHS.includes(path) || PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix) && path.length > prefix.length);
}
